/*
  Horizontale carousel voor de FitTowel review-sectie.
  Zonder JavaScript blijft de track gewoon scrollbaar — de knoppen zijn een
  hulpmiddel, geen voorwaarde om de reviews te kunnen lezen.
*/
if (!customElements.get('ft-carousel')) {
  customElements.define(
    'ft-carousel',
    class FtCarousel extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('[data-carousel-track]');
        this.prev = this.querySelector('[data-carousel-prev]');
        this.next = this.querySelector('[data-carousel-next]');

        if (!this.track) return;

        this.onPrev = () => this.scrollByPage(-1);
        this.onNext = () => this.scrollByPage(1);
        this.onScroll = () => this.updateButtons();

        this.prev?.addEventListener('click', this.onPrev);
        this.next?.addEventListener('click', this.onNext);
        this.track.addEventListener('scroll', this.onScroll, { passive: true });

        // De breedte van een kaart verandert mee met de breakpoints.
        this.resizeObserver = new ResizeObserver(() => this.updateButtons());
        this.resizeObserver.observe(this.track);

        this.updateButtons();
      }

      disconnectedCallback() {
        this.prev?.removeEventListener('click', this.onPrev);
        this.next?.removeEventListener('click', this.onNext);
        this.track?.removeEventListener('scroll', this.onScroll);
        this.resizeObserver?.disconnect();
        cancelAnimationFrame(this.raf);
      }

      get step() {
        const card = this.track.querySelector(':scope > *');
        if (!card) return this.track.clientWidth;

        const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
        return card.getBoundingClientRect().width + gap;
      }

      get prefersReducedMotion() {
        return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      }

      scrollByPage(direction) {
        this.animateTo(this.track.scrollLeft + this.step * direction);
      }

      /*
        Eigen animatie in plaats van scrollBy({behavior:'smooth'}). Dat native
        gladde scrollen kwam in deze carousel niet van zijn plek: de positie
        sprong meteen naar het snappunt van de eerste kaart (scrollLeft 4, de
        horizontale padding van de track) en bleef daar staan — gemeten over
        twaalf frames, ongeacht of scroll-snap op mandatory, proximity of none
        stond. Een instant scroll werkte wél. Deze animatie zet het snappen
        tijdens de beweging uit en herstelt het daarna, zodat de eindpositie
        alsnog netjes op een kaart uitkomt.
      */
      animateTo(target) {
        const track = this.track;
        const start = track.scrollLeft;
        const max = track.scrollWidth - track.clientWidth;
        const end = Math.max(0, Math.min(max, target));
        const distance = end - start;

        if (Math.abs(distance) < 1) return;

        if (this.prefersReducedMotion) {
          track.scrollLeft = end;
          return;
        }

        cancelAnimationFrame(this.raf);
        track.style.scrollSnapType = 'none';

        const duration = 400;
        const startTime = performance.now();

        const frame = (now) => {
          const progress = Math.min(1, (now - startTime) / duration);
          // ease-in-out kwadratisch
          const eased =
            progress < 0.5
              ? 2 * progress * progress
              : 1 - Math.pow(-2 * progress + 2, 2) / 2;

          track.scrollLeft = start + distance * eased;

          if (progress < 1) {
            this.raf = requestAnimationFrame(frame);
          } else {
            track.style.scrollSnapType = '';
          }
        };

        this.raf = requestAnimationFrame(frame);
      }

      updateButtons() {
        const { scrollLeft, scrollWidth, clientWidth } = this.track;
        // 1px marge: browsers ronden scrollposities af op subpixels.
        const atStart = scrollLeft <= 1;
        const atEnd = scrollLeft + clientWidth >= scrollWidth - 1;
        const noOverflow = scrollWidth <= clientWidth + 1;

        if (this.prev) this.prev.disabled = atStart;
        if (this.next) this.next.disabled = atEnd;

        this.classList.toggle('ft-carousel--static', noOverflow);
      }
    }
  );
}
